import { IconBellFilled } from '@tabler/icons-react';
import {
  NotificationType,
  Notifications as NotificationsSection
} from '@/ui/Toolbar/Notifications';
import { useState } from 'react';
import { t } from 'i18next';

import { ActionIcon, Indicator, Popover, Text } from '@mantine/core';
import { useNotification } from '@/hooks/useNotification';
import { useNavigate } from 'react-router-dom';
import { useDateFormat } from '@/hooks/useLang';
import { useSmallerView } from '@/hooks/useSmallerView';
import { useBodyScroll } from '@/hooks/useBodyScroll';

const getNotifications = (
  notifications: NotificationType[],
  formatDate: (date: string, pattern: string) => string
) => {
  return notifications.map((it) => {
    const date = formatDate(it.createdAt, 'LL');

    const notification: NotificationType = {
      type: 'success',
      title: it.title,
      read: it.read,
      _id: it._id,
      content: it.content,
      description: '',
      createdAt: it.createdAt,
      case: it.type
    };

    const err = it.content?.errCode;
    const type = it.content?.type;

    switch (it.type) {
      case 'DOCUMENT':
        notification.type = err ? 'error' : 'normal';
        notification.title = (
          <>
            {t('Notifications.document.error')} {it.content.service}
          </>
        );
        notification.description = <Text>{it.content.filename}</Text>;
        break;
      case 'ERROR':
        notification.title = <>{t(`notifications.${type}.title`, type)}</>;
        notification.description = <>{t(`notifications.${type}.content`, type)}</>;
        notification.type = 'error';
        break;
      case 'DATA':
        notification.description = <Text>{it.content.label}</Text>;
        notification.title = <>{t(`notifications.onNewData.${type}`, type)}</>;
        break;
      case 'GENERIC':
        if (type === 'MARKETPLACE-PAYMENT') {
          notification.description = (
            <>
              <Text c="dimmed" size="xs">
                {date}
              </Text>
              <Text>{it.content.productTitle}</Text>
            </>
          );
          notification.title = <>{t(`notifications.${type}.title`, type)}</>;
        }
        break;
      default:
        notification.title = 'UNSUPPORTED';
        notification.description = '';
        break;
    }
    return notification;
  });
};

export const Notifications = () => {
  const [opened, setOpened] = useState(false);

  const { notifications, clearAll, readAll, clearX, readX } = useNotification();
  const navigate = useNavigate();
  const formatDate = useDateFormat();

  useBodyScroll(opened);

  const data = getNotifications(notifications, formatDate);

  const indicator = (
    <Indicator
      size={8}
      variant="filled"
      color="red.4"
      inline
      processing
      disabled={data.length === 0}>
      <ActionIcon color="white" onClick={() => setOpened((prev) => !prev)}>
        <IconBellFilled size={24} stroke={2} />
      </ActionIcon>
    </Indicator>
  );

  const isMobile = useSmallerView('xs');

  const content = (
    <NotificationsSection
      fromDrawer={isMobile}
      onClick={(notification) => {
        readX({
          id: notification._id,
          onAction: () => {
            if (notification.case === 'DATA') {
              navigate('/page/' + notification.content._id);
            } else if (notification.case === 'GENERIC') {
              if (notification.content.type === 'MARKETPLACE-PAYMENT') {
                navigate('/orders');
              } else navigate('/history/product/1?filters=blockchain.none');
            }
          }
        });
        setOpened(false);
      }}
      onDeleteAll={clearAll}
      onReadAll={readAll}
      data={data}
      onRemoveClick={(notification) => clearX(notification._id)}
    />
  );

  return (
    <>
      <Popover
        closeOnEscape
        closeOnClickOutside
        onClose={() => setOpened(false)}
        opened={opened}
        offset={2}
        arrowSize={20}
        withArrow
        position="bottom"
        zIndex={1007}
        shadow="xl">
        <Popover.Target>{indicator}</Popover.Target>
        <Popover.Dropdown>{content}</Popover.Dropdown>
      </Popover>
    </>
  );
};
// ?clearX
